import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, ArrowLeft, Check, Copy, CopyCheck, Loader2, MessageSquareQuote, Pencil, Plus, Search, Users, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KbReviewerSelect } from "@/components/KbReviewerSelect";
import { useAuth } from "@/context/AuthContext";

/** Snippets library: short support reply text, browsable by topic and one click
 *  from the clipboard into Intercom. */
export default function KBSnippets() {
  const [data, setData] = useState(null);
  const [topic, setTopic] = useState("");
  const [q, setQ] = usePersistedState("kb-snippets.q", "");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const [dupes, setDupes] = useState(null);
  const [showDupes, setShowDupes] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = usePersistedState("kb-snippets.sort", "name");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (topic) params.set("topic", topic);
      if (q.trim()) params.set("q", q.trim());
      const { data: d } = await api.get(`/kb/snippets/library?${params}`);
      setData(d);
    } catch {
      toast.error("Couldn't load the snippets");
    } finally { setLoading(false); }
  }, [topic, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const loadDupes = useCallback(async () => {
    try {
      const { data: d } = await api.get("/kb/snippets/duplicates");
      setDupes(d);
    } catch { /* the chip just stays hidden */ }
  }, []);
  useEffect(() => { loadDupes(); }, [loadDupes]);

  const archive = async (s) => {
    try {
      await api.post(`/kb/articles/${s.slug}/archive`, { archived: true, reason: "Duplicate snippet" });
      toast.success(`Archived "${s.name}"`);
      loadDupes();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't archive that snippet");
    }
  };

  const saveTags = async (s, tags) => {
    // Optimistic: the card feels instant, and a failure re-reads the truth.
    setData((d) => ({ ...d, snippets: d.snippets.map((x) => (x.slug === s.slug ? { ...x, topics: tags } : x)) }));
    try {
      await api.put(`/kb/snippets/${s.slug}/tags`, { tags });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the tags");
      load();
    }
  };

  const copy = async (s) => {
    try {
      await navigator.clipboard.writeText(s.text);
      setCopied(s.article_id);
      setTimeout(() => setCopied(""), 1600);
      toast.success("Snippet copied");
      // Counts what support actually pastes, so dead snippets can be retired.
      api.post(`/kb/snippets/${s.slug}/used`)
        .then(({ data: r }) => setData((d) => ({
          ...d,
          total_uses: r.total_uses ?? (d.total_uses || 0) + 1,
          snippets: d.snippets.map((x) => (x.slug === s.slug
            ? { ...x, uses: r.uses, people: r.people, last_used: r.last_used } : x)),
        })))
        .catch(() => { /* a lost count must never break the copy */ });
    } catch {
      toast.error("Clipboard blocked by the browser");
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6" data-testid="kb-snippets-page">
      <Link to="/kb" className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 dark:text-slate-400 dark:hover:text-slate-100">
        <ArrowLeft className="h-3.5 w-3.5" /> Knowledge Base
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-100 dark:text-slate-50">
            <MessageSquareQuote className="h-6 w-6" /> Snippets library
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Short reply text for support conversations — staff only, kept out of the article tree.
            Copy one and paste it straight into Intercom.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="kb-snippets-new">
          <Plus className="mr-1 h-4 w-4" /> New snippet
        </Button>
      </div>

      {creating && <NewSnippetDialog topics={(data?.topics || []).map(([t]) => t)}
        onClose={() => setCreating(false)} />}

      {!!(dupes?.groups?.length || dupes?.overlaps?.length) && (
        <div>
          <button type="button" onClick={() => setShowDupes((v) => !v)} data-testid="kb-snippets-dupes-chip"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <CopyCheck className="h-3.5 w-3.5" />
            {dupes.groups.length
              ? `${dupes.duplicate_count} snippets in ${dupes.groups.length} possible duplicate ${dupes.groups.length === 1 ? "group" : "groups"}`
              : `${dupes.overlaps.length} overlapping ${dupes.overlaps.length === 1 ? "pair" : "pairs"} worth a look — no clear duplicates`}
            <span className="text-amber-600 dark:text-amber-400">{showDupes ? "hide" : "review"}</span>
          </button>
          {showDupes && (
            <div className="mt-3 space-y-3" data-testid="kb-snippets-dupes">
              {[...dupes.groups, ...dupes.overlaps].map((g, gi) => (
                <Card key={gi} className="p-4" data-testid={`kb-snippet-dupe-group-${gi}`}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                    {g.kind === "duplicate" ? "Possible duplicate" : "Overlapping wording"} · ~{g.similarity_pct}% shared wording · {g.snippets.length} snippets
                  </div>
                  <div className="mt-2 grid gap-3 md:grid-cols-2">
                    {g.snippets.map((s) => (
                      <div key={s.slug} className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 dark:border-slate-700"
                        data-testid={`kb-snippet-dupe-${s.slug}`}>
                        <div className="flex items-start gap-2">
                          <Link to={`/kb/a/${s.slug}`} className="text-sm font-semibold text-slate-800 dark:text-slate-100 hover:underline dark:text-slate-100">
                            {s.name}
                          </Link>
                          <span className="ml-auto shrink-0 text-[10px] text-slate-400 dark:text-slate-500">{s.words} words</span>
                        </div>
                        <p className="mt-1.5 line-clamp-6 text-xs text-slate-600 dark:text-slate-300">{s.text}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <Link to={`/kb/a/${s.slug}/edit`}>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                              data-testid={`kb-snippet-dupe-edit-${s.slug}`}>
                              <Pencil className="mr-1 h-3 w-3" /> Keep &amp; edit
                            </Button>
                          </Link>
                          <Button size="sm" variant="ghost" onClick={() => archive(s)}
                            data-testid={`kb-snippet-dupe-archive-${s.slug}`}
                            className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/40">
                            <Archive className="mr-1 h-3 w-3" /> Archive
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 dark:text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
          placeholder="Search snippet titles and text" data-testid="kb-snippets-search" />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
        <span data-testid="kb-snippets-usage-note">
          {data?.total_uses ? `${data.total_uses} ${data.total_uses === 1 ? "copy" : "copies"} recorded` : "No copies recorded yet"}
          {data?.tracking_since ? ` · counting since ${String(data.tracking_since).slice(0, 10)}` : ""}
        </span>
        <button type="button" onClick={() => setSort(sort === "used" ? "name" : "used")}
          data-testid="kb-snippets-sort"
          className="rounded-full border border-slate-200 dark:border-slate-800 px-2.5 py-1 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-800">
          Sort: {sort === "used" ? "most copied" : "A–Z"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="kb-snippets-topics">
        <button type="button" onClick={() => setTopic("")} data-testid="kb-snippets-topic-all"
          className={`rounded-full border px-2.5 py-1 text-xs font-medium ${topic === ""
            ? "border-primary bg-primary/10 text-primary"
            : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
          All {data ? `(${data.total})` : ""}
        </button>
        {(data?.topics || []).map(([name, count]) => (
          <button key={name} type="button" onClick={() => setTopic(name === topic ? "" : name)}
            data-testid={`kb-snippets-topic-${name}`}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium ${topic === name
              ? "border-primary bg-primary/10 text-primary"
              : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
            {name} ({count})
          </button>
        ))}
        {loading && <Loader2 className="ml-1 h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {[...(data?.snippets || [])].sort((a, b) => (sort === "used"
          ? b.uses - a.uses || a.name.localeCompare(b.name)
          : a.name.localeCompare(b.name))).map((s) => (
          <Card key={s.article_id} className="flex flex-col p-4" data-testid={`kb-snippet-${s.slug}`}>
            <div className="flex items-start gap-2">
              <Link to={`/kb/a/${s.slug}`} className="font-semibold text-slate-800 dark:text-slate-100 hover:underline dark:text-slate-100">
                {s.name}
              </Link>
              {s.draft && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">Draft</span>}
              <Link to={`/kb/a/${s.slug}/edit`} className="ml-auto shrink-0"
                data-testid={`kb-snippet-edit-${s.slug}`}>
                <Button size="sm" variant="ghost" className="h-7 px-2" title="Edit the wording">
                  <Pencil className="h-3.5 w-3.5" /><span className="ml-1 text-xs">Edit</span>
                </Button>
              </Link>
              <Button size="sm" variant="outline" className="h-7 shrink-0"
                onClick={() => copy(s)} data-testid={`kb-snippet-copy-${s.slug}`}>
                {copied === s.article_id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                <span className="ml-1 text-xs">{copied === s.article_id ? "Copied" : "Copy"}</span>
              </Button>
            </div>
            <p className="mt-2 line-clamp-4 text-sm text-slate-600 dark:text-slate-300">{s.text}</p>
            <UsageLine snippet={s} since={data?.tracking_since} />
            <TagEditor snippet={s} onSave={saveTags} />
          </Card>
        ))}
        {!loading && !(data?.snippets || []).length && (
          <Card className="p-6 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="kb-snippets-empty">
            No snippets match that.
          </Card>
        )}
      </div>
    </div>
  );
}

/** Copy counts, per person, with an honest note about when counting started. */
function UsageLine({ snippet: s, since }) {
  const days = since ? Math.floor((Date.now() - new Date(since)) / 86400000) : 0;
  const who = (s.people || []).map((p) => `${p.name} ×${p.count}`).join(", ");
  const last = s.last_used ? new Date(s.last_used) : null;
  const ago = last ? Math.floor((Date.now() - last) / 86400000) : null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400"
      data-testid={`kb-snippet-usage-${s.slug}`}>
      {s.uses > 0 ? (
        <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
          title={who ? `Copied by ${who}` : ""}>
          <Users className="h-3 w-3" /> copied {s.uses}×
          {ago !== null && <span className="font-normal">· {ago === 0 ? "today" : `${ago}d ago`}</span>}
        </span>
      ) : days >= 14 ? (
        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-semibold text-slate-500 dark:text-slate-400 dark:bg-slate-800 dark:text-slate-400">
          never copied
        </span>
      ) : (
        <span>no copies yet (counting for {days}d)</span>
      )}
      {!!(s.embeds || []).length && (
        <span title={s.embeds.map((e) => e.name).join(", ")}>
          embedded in {s.embeds.length} article{s.embeds.length === 1 ? "" : "s"}
        </span>
      )}
      {who && <span className="truncate">{who}</span>}
    </div>
  );
}

/** Title (+ optional topic) then straight into the editor. */
function NewSnippetDialog({ topics, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim() || saving) return;
    if (!reviewer && !isAdmin) { toast.error("Pick a reviewer — every article and snippet needs one"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/kb/snippets", { name: name.trim(), topic: topic.trim(), reviewer_id: reviewer || undefined });
      toast.success("Snippet created — write the wording");
      navigate(`/kb/a/${data.slug}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the snippet");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="kb-snippet-new-dialog">
        <DialogHeader>
          <DialogTitle>New snippet</DialogTitle>
          <DialogDescription>
            Give it a title and an optional topic — the editor opens next.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Title</label>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") create(); }}
              placeholder="e.g. Porting timelines" data-testid="kb-snippet-new-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Topic (optional)</label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} list="kb-snippet-topics"
              placeholder="e.g. numbers" data-testid="kb-snippet-new-topic" />
            <datalist id="kb-snippet-topics">
              {topics.filter((t) => t !== "untagged").map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Reviewer {isAdmin ? <span className="font-normal text-slate-400">(optional for super admins)</span> : <span className="text-amber-600">*</span>}
            </label>
            <KbReviewerSelect value={reviewer} onChange={setReviewer} allowSelf={isAdmin} testid="kb-snippet-new-reviewer" />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            It's created as a draft in the Snippets library, staff-only.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="kb-snippet-new-cancel">Cancel</Button>
          <Button onClick={create} disabled={!name.trim() || saving || (!reviewer && !isAdmin)} data-testid="kb-snippet-new-create">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Create &amp; edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Inline tags on a snippet card: type and press Enter to add, × to remove. */
function TagEditor({ snippet, onSave }) {
  const [draft, setDraft] = useState("");
  const tags = snippet.topics || [];

  const add = () => {
    const tag = draft.trim().toLowerCase();
    if (!tag) return;
    setDraft("");
    if (!tags.includes(tag)) onSave(snippet, [...tags.filter((t) => t !== "untagged"), tag]);
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1" data-testid={`kb-snippet-tags-${snippet.slug}`}>
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded bg-slate-100 dark:bg-slate-800 py-0.5 pl-1.5 pr-1 text-[10px] font-medium text-slate-600 dark:text-slate-300 dark:bg-slate-800 dark:text-slate-300">
          {t}
          <button type="button" title={`Remove "${t}"`} data-testid={`kb-snippet-tag-remove-${snippet.slug}-${t}`}
            onClick={() => onSave(snippet, tags.filter((x) => x !== t))}
            className="text-slate-400 dark:text-slate-500 hover:text-rose-600">
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      <span className="inline-flex items-center">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="add tag" data-testid={`kb-snippet-tag-input-${snippet.slug}`}
          className="w-20 rounded border border-dashed border-slate-300 dark:border-slate-700 bg-transparent px-1.5 py-0.5 text-[10px] text-slate-600 dark:text-slate-300 placeholder:text-slate-400 focus:w-28 focus:border-solid focus:border-primary focus:outline-none dark:border-slate-600 dark:text-slate-200" />
        {draft && (
          <button type="button" onClick={add} data-testid={`kb-snippet-tag-add-${snippet.slug}`}
            className="ml-0.5 text-slate-400 dark:text-slate-500 hover:text-primary"><Plus className="h-3 w-3" /></button>
        )}
      </span>
    </div>
  );
}
